import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Building2,
    Users,
    Mail,
    School,
    ClipboardCheck,
    GraduationCap,
    ChevronDown,
    LogOut,
    Menu,
    X,
    ClipboardList,
    ScrollText,
    NotebookPen,
    Trophy,
    Compass,
    Presentation,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const ROLE_LABELS = {
    platform_admin: "Platform Admin",
    reviewer: "Reviewer",
    org_leader: "Org Leader",
    coach: "Coach",
    teacher: "Teacher",
};

function buildNav(user) {
    const base = [
        { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    ];
    if (user.role === "platform_admin") {
        base.push(
            { to: "/app/cycles", label: "Cycles", icon: ClipboardList, testId: "nav-cycles" },
            { to: "/app/admin/organizations", label: "Organizations", icon: Building2, testId: "nav-orgs" },
            { to: "/app/admin/invitations/new", label: "Invitations", icon: Mail, testId: "nav-invitations" },
            { to: "/app/admin/audit-log", label: "Audit log", icon: ScrollText, testId: "nav-audit-log" },
            { to: "/app/admin/deck", label: "Client Deck", icon: Presentation, testId: "nav-deck" },
        );
    } else if (user.role === "org_leader") {
        base.push(
            { to: "/app/cycles", label: "Cycles", icon: ClipboardList, testId: "nav-cycles" },
        );
        if (user.org_id) {
            base.push(
                { to: `/app/admin/organizations/${user.org_id}`, label: "My Organization", icon: School, testId: "nav-my-org" },
            );
        }
        base.push(
            { to: "/app/admin/invitations/new", label: "Invitations", icon: Mail, testId: "nav-invitations" },
            { to: "/app/admin/audit-log", label: "Audit log", icon: ScrollText, testId: "nav-audit-log" },
        );
    } else if (user.role === "reviewer") {
        base.push(
            { to: "/app/cycles", label: "Assigned Cycles", icon: ClipboardCheck, testId: "nav-cycles" },
        );
    } else if (user.role === "coach") {
        base.push(
            { to: "/app/cycles", label: "Cycles", icon: ClipboardList, testId: "nav-cycles" },
        );
    } else if (user.role === "teacher") {
        base.push(
            { to: "/app/teacher/reflections", label: "My Reflections", icon: NotebookPen, testId: "nav-my-reflections" },
            { to: "/app/teacher/my-scores", label: "My Scores", icon: Trophy, testId: "nav-my-scores" },
        );
    }
    // Leadership Reflection Framework — available to every non-teacher role.
    if (user.role !== "teacher") {
        base.push(
            { to: "/app/lrf", label: "Leadership Reflection", icon: Compass, testId: "nav-lrf" },
        );
    }
    return base;
}

export default function AppShell() {
    const { user, logout } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();

    if (!user) return null;
    const nav = buildNav(user);
    const initials =
        (user.full_name || user.email || "?")
            .split(/\s+|\./)
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0]?.toUpperCase())
            .join("") || "?";

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F1F6FB] text-[#0E1B2C] flex" data-testid="app-shell">
            {/* Sidebar (desktop) */}
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-[#0B2545] text-white transform transition-transform lg:translate-x-0 ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="sidebar"
            >
                <div className="h-16 px-6 flex items-center justify-between border-b border-white/10">
                    <Link
                        to="/app/dashboard"
                        className="flex items-center gap-3 font-serif text-lg"
                        data-testid="sidebar-brand"
                    >
                        <img
                            src="/esss-logo.png?v=3"
                            alt="ESSS"
                            className="h-9 w-9 rounded-md object-contain bg-white"
                            data-testid="sidebar-logo"
                        />
                        <span className="font-semibold tracking-tight">ESSS Coherence</span>
                    </Link>
                    <button
                        onClick={() => setMobileOpen(false)}
                        className="lg:hidden text-white/70 hover:text-white"
                        aria-label="Close menu"
                        data-testid="sidebar-close-btn"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="px-6 py-5 border-b border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
                        Signed in as
                    </div>
                    <div className="font-semibold text-sm">{user.full_name || user.email}</div>
                    <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15">
                            {ROLE_LABELS[user.role] || user.role}
                        </span>
                        {user.organization?.name && (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8AC4FF]/20 border border-[#8AC4FF]/30 text-[#D6E6F2]"
                                data-testid="sidebar-org-name"
                            >
                                {user.organization.name}
                            </span>
                        )}
                    </div>
                </div>

                <nav className="p-4 space-y-1" data-testid="sidebar-nav">
                    {nav.map((n) => {
                        const Icon = n.icon;
                        const inner = (
                            <>
                                <Icon size={16} />
                                <span>{n.label}</span>
                                {n.stub && (
                                    <span className="ml-auto text-[10px] uppercase tracking-widest text-white/50">
                                        Soon
                                    </span>
                                )}
                            </>
                        );
                        if (n.stub) {
                            return (
                                <button
                                    key={n.testId}
                                    disabled
                                    data-testid={n.testId}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/50 cursor-not-allowed"
                                >
                                    {inner}
                                </button>
                            );
                        }
                        return (
                            <NavLink
                                key={n.testId}
                                to={n.to}
                                end
                                data-testid={n.testId}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                        isActive
                                            ? "bg-white text-[#0B2545] font-semibold"
                                            : "text-white/85 hover:bg-white/10"
                                    }`
                                }
                                onClick={() => setMobileOpen(false)}
                            >
                                {inner}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="mt-auto p-4 text-[11px] text-white/40 absolute bottom-0 inset-x-0 border-t border-white/10">
                    © ESSS · Proprietary Framework Portfolio
                </div>
            </aside>

            {/* Backdrop for mobile */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-30 lg:hidden"
                    onClick={() => setMobileOpen(false)}
                    aria-hidden
                />
            )}

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header
                    className="h-16 bg-white border-b border-[#D6E6F2] px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20"
                    data-testid="app-topbar"
                >
                    <div className="flex items-center gap-3">
                        <button
                            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-[#D6E6F2] text-[#0B2545]"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open menu"
                            data-testid="topbar-menu-btn"
                        >
                            <Menu size={18} />
                        </button>
                        <div className="text-xs uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                            {ROLE_LABELS[user.role] || user.role}
                        </div>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D6E6F2] hover:bg-[#F1F6FB] transition-colors"
                                data-testid="user-menu-trigger"
                            >
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0B2545] text-white text-xs font-bold">
                                    {initials}
                                </span>
                                <span className="hidden sm:inline text-sm font-medium text-[#0B2545]">
                                    {user.full_name || user.email}
                                </span>
                                <ChevronDown size={14} className="text-[#0B2545]/60" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>
                                <div className="text-sm font-semibold text-[#0B2545]">
                                    {user.full_name || user.email}
                                </div>
                                <div className="text-xs text-[#0E1B2C]/60">{user.email}</div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled data-testid="user-menu-profile">
                                Profile (coming soon)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={handleLogout}
                                className="text-red-600 focus:text-red-600"
                                data-testid="user-menu-logout"
                            >
                                <LogOut size={14} />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </header>

                <main className="flex-1 p-4 lg:p-10 max-w-6xl w-full mx-auto" data-testid="app-main">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
